import { useTranslation } from 'react-i18next'
import { motion } from 'framer-motion'
import { Calendar } from 'lucide-react'
import { PageSeo, HeroSimple, SectionHeading, TreatmentCard, Button, Breadcrumbs, FeatureList } from '@/components/ui'
import { LocalizedLink } from '@/components/LocalizedLink'
import { getTreatmentPath, getCategoryKeyFromTreatmentSlug } from '@/lib/treatments'
import { localizedRoutes } from '@/i18n/routes'
import type { RouteKey } from '@/i18n/routes'
import type { SupportedLanguage } from '@/i18n'

interface TreatmentItem {
  key: string
  title: string
  description: string
  image: string
  imageAlt: string
  slug: string
}

interface CategoryPageTemplateProps {
  categoryKey: RouteKey
  namespace: string
}

export function CategoryPageTemplate({ categoryKey, namespace }: CategoryPageTemplateProps) {
  const { t, i18n } = useTranslation([namespace, 'common'])
  const lang = i18n.language as SupportedLanguage

  const title = t(`${namespace}:hero.title`)
  const subtitle = t(`${namespace}:hero.subtitle`)
  const watermark = t(`${namespace}:hero.watermark`, '')

  const introTitle = t(`${namespace}:intro.title`)
  const introParagraphs = t(`${namespace}:intro.paragraphs`, { returnObjects: true }) as string[]
  const introImage = t(`${namespace}:intro.image`)
  const introImageAlt = t(`${namespace}:intro.imageAlt`)

  const treatmentsTitle = t(`${namespace}:treatments.title`)
  const treatmentsSubtitle = t(`${namespace}:treatments.subtitle`)
  const treatments = t(`${namespace}:treatments.items`, { returnObjects: true }) as TreatmentItem[]

  const ctaTitle = t(`${namespace}:cta.title`)
  const ctaSubtitle = t(`${namespace}:cta.subtitle`)
  const ctaButton = t(`${namespace}:cta.button`)

  const featuresBundle = (i18n.getResourceBundle(lang, namespace) as Record<string, unknown>)?.features as
    | { title?: string; subtitle?: string; items?: { title: string; description: string }[] }
    | undefined
  const featuresTitle = featuresBundle?.title || ''
  const featuresSubtitle = featuresBundle?.subtitle || ''
  const featuresItems = Array.isArray(featuresBundle?.items) ? featuresBundle.items : []
  const hasFeatures = featuresItems.length > 0

  const categoryLabel = t(`common:routes.${categoryKey}`)

  const breadcrumbNavItems = [
    { label: t('common:routes.home'), to: 'home' as RouteKey },
    { label: t('common:nav.services') },
    { label: categoryLabel },
  ]

  const breadcrumbSchemaItems = [
    { name: t('common:routes.home'), path: getLocalizedPathFromRouteKey('home', lang) },
    { name: t('common:nav.services') },
    { name: categoryLabel },
  ]

  const canonicalUrl = typeof window !== 'undefined' ? window.location.href : ''
  const baseUrl = canonicalUrl.split('/').slice(0, 3).join('/')

  const medicalProcedures = treatments.map((item) => ({
    '@context': 'https://schema.org',
    '@type': 'MedicalProcedure',
    name: item.title,
    description: item.description,
    url: `${baseUrl}${getTreatmentPath(categoryKey, item.slug, lang)}`,
    procedureType: item.title,
    performedBy: {
      '@type': 'Physician',
      name: "Dr. Alessandro Dall'Antonia",
      worksFor: { '@type': 'MedicalBusiness', name: 'AD Aesthetics' },
    },
  }))

  const jsonLd = [
    {
      '@context': 'https://schema.org',
      '@type': 'MedicalWebPage',
      name: title,
      description: t(`${namespace}:seo.description`),
      url: canonicalUrl,
    },
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: breadcrumbSchemaItems.map((item, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: item.name,
        item: item.path ? `${baseUrl}${item.path}` : undefined,
      })),
    },
    ...medicalProcedures,
  ]

  return (
    <>
      <PageSeo
        title={t(`${namespace}:seo.title`)}
        description={t(`${namespace}:seo.description`)}
        jsonLd={jsonLd}
        breadcrumb={breadcrumbSchemaItems}
      />

      <main>
        <HeroSimple
          title={title}
          subtitle={subtitle}
          variant="dark"
          watermark={
            watermark ? (
              <span className="font-display text-[12rem] md:text-[18rem] lg:text-[24rem] font-semibold italic text-primary/[0.05] leading-none">
                {watermark}
              </span>
            ) : null
          }
        />

        <Breadcrumbs items={breadcrumbNavItems} />

        {/* Intro */}
        <section className="bg-cream py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6 }}
              >
                <SectionHeading title={introTitle} />
                <div className="mt-6 space-y-4">
                  {introParagraphs.map((paragraph, index) => (
                    <p key={index} className="text-muted leading-relaxed">{paragraph}</p>
                  ))}
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                whileInView={{ opacity: 1 }}
                viewport={{ once: true, margin: '-50px' }}
                transition={{ duration: 0.6 }}
                className="aspect-[4/3] rounded-xl overflow-hidden"
              >
                <img
                  src={introImage}
                  alt={introImageAlt}
                  loading="lazy"
                  className="w-full h-full object-cover"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* Features */}
        {hasFeatures && (
          <section className="bg-white py-16 md:py-24">
            <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
              <FeatureList
                title={featuresTitle}
                subtitle={featuresSubtitle}
                items={featuresItems}
              />
            </div>
          </section>
        )}

        {/* Treatments */}
        <section className="bg-cream py-16 md:py-24">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeading
              title={treatmentsTitle}
              subtitle={treatmentsSubtitle}
              centered
              className="mb-12 md:mb-16"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              {treatments.map((item, index) => {
                const isMapped = getCategoryKeyFromTreatmentSlug(item.slug, lang) === categoryKey
                return (
                  <motion.div
                    key={item.key}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: '-50px' }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="h-full"
                  >
                    <TreatmentCard
                      to={isMapped ? getTreatmentPath(categoryKey, item.slug, lang) : undefined}
                      disabled={!isMapped}
                      title={item.title}
                      description={item.description}
                      imageSrc={item.image}
                      imageAlt={item.imageAlt}
                    />
                  </motion.div>
                )
              })}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary py-16 md:py-24">
          <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display text-h2 font-semibold text-ink">{ctaTitle}</h2>
            <p className="mt-4 text-lead text-ink/80 max-w-2xl mx-auto">{ctaSubtitle}</p>
            <div className="mt-8">
              <LocalizedLink to="bookConsultation">
                <Button variant="primary-light">
                  <Calendar className="w-4 h-4" />
                  {ctaButton}
                </Button>
              </LocalizedLink>
            </div>
          </div>
        </section>
      </main>
    </>
  )
}

function getLocalizedPathFromRouteKey(routeKey: RouteKey, lang: SupportedLanguage): string {
  const path = localizedRoutes[routeKey][lang]
  return path ? `/${lang}/${path}` : `/${lang}`
}
